import React, {useState, useEffect, useMemo} from 'react'
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Wallet,
  X,
  GripVertical,
  Trash2,
  Copy,
  Ship,
  MoreHorizontal,
  Edit,
} from 'lucide-react'
import {useAuth} from '../../auth/core/Auth'
import {useNavigate, useLocation, Link} from 'react-router-dom'
import axios from 'axios'
import {setupAxios} from '../../auth'
import {ACCOUNTS_PERMS} from '../core/permissions'
import {getVesselList, getCompanyAdminList, getCompanyList} from '../../Management/core/_requests'
import {
  AccountsActionDropdown,
  DropdownPosition,
  getDropdownPosition,
} from './AccountsActionDropdown'
import {useGlobalFilter} from '../../../shared/context/GlobalFilterContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const api = axios.create({baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || ''})
setupAxios(api)

const getErrorMessage = (err: any, fallback: string) => {
  return err?.response?.data?.message || err?.message || fallback
}

export default function AccountsBudgetExpense() {
  const {isSuperAdmin, hasPermission, currentUser} = useAuth()
  const roleId = Number((currentUser as any)?.role?.id ?? 0) || 0
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isPoModalOpen, setIsPoModalOpen] = useState(false)
  const [selectedPos, setSelectedPos] = useState<any[]>([])
  const [selectedPoAccount, setSelectedPoAccount] = useState('')
  const [reports, setReports] = useState<any[]>([])
  const [editingBudget, setEditingBudget] = useState<any | null>(null)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null)
  const {state: globalState, vessels: globalVessels} = useGlobalFilter()

  const formatBudgetMonth = (value: any) => {
    if (!value) return ''
    const parsed =
      typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)
        ? new Date(`${value}-01T00:00:00Z`)
        : new Date(value)
    if (Number.isNaN(parsed.getTime())) return ''
    return new Intl.DateTimeFormat('en-US', {month: 'short', year: 'numeric'}).format(parsed)
  }

  // Form states
  const [adminType, setAdminType] = useState('')
  const [selectedAdminId, setSelectedAdminId] = useState('')
  const [selectedVesselId, setSelectedVesselId] = useState('')
  const [vessels, setVessels] = useState<any[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<any[]>([])
  const [companyGroupAdmins, setCompanyGroupAdmins] = useState<any[]>([])
  const [availableCodes, setAvailableCodes] = useState<any[]>([])

  const filteredVessels = useMemo(() => {
    if (isSuperAdmin) {
      if (!adminType || !selectedAdminId) return []
      return vessels.filter((v: any) => {
        const isActive = v?.active ?? true
        if (!isActive) return false
        if (adminType === 'ca') {
          const caId = Number(
            v.companyAdminId || v.companyId || v.companyAdmin?.id || v.companyAdmin
          )
          return caId === Number(selectedAdminId)
        } else if (adminType === 'cga') {
          const cgaId = Number(v.companyGroupAdminId || v.companyGroupId || v.companyGroupAdmin?.id)
          return cgaId === Number(selectedAdminId)
        }
        return true
      })
    }
    return vessels
  }, [vessels, isSuperAdmin, adminType, selectedAdminId])
  const [budgetItems, setBudgetItems] = useState<
    Array<{id: number; accountCode: string; amount: string; isOneTime?: boolean}>
  >([{id: Date.now(), accountCode: '', amount: '', isOneTime: false}])
  const [budgetDetails, setBudgetDetails] = useState('')
  const [budgetMonth, setBudgetMonth] = useState('')
  const [expandedVessels, setExpandedVessels] = useState<Record<string, boolean>>({})
  const [expandedBudgets, setExpandedBudgets] = useState<Record<string, boolean>>({})
  const [budgetTimelines, setBudgetTimelines] = useState<Record<string, any[]>>({})
  const [timelineLoading, setTimelineLoading] = useState<Record<string, boolean>>({})
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  const buildReportsFromBudgets = (rows: any[]) => {
    return rows.map((row: any) => {
      const vesselName =
        row.vessel?.fleetName ||
        row.vessel?.fleet_name ||
        row.vessel?.imoNumber ||
        row.vessel?.imo_number ||
        String(row.vesselId || row.vessel_id || '')
      const budgetMonth = row.budgetMonth || row.budget_month
      const createdAt = row.createdAt || row.created_at || new Date().toISOString()
      const monthLabel = formatBudgetMonth(budgetMonth) || String(createdAt).split('T')[0]

      const totalBudget = Number(row.totalBudget ?? row.total_budget ?? row.budget ?? 0)
      const baseBudget = Number(row.baseBudget ?? row.base_budget ?? row.budget ?? 0)
      const totalExpenses = Number(row.spentAmount ?? row.spent_amount ?? row.spent ?? 0)
      const remainingAmount = Number(
        row.remainingAmount ?? row.remaining_amount ?? totalBudget - totalExpenses
      )

      return {
        id: `BUD-${row.id}`,
        rawId: row.id,
        adminName: '-',
        vesselName,
        vesselId: row.vesselId || row.vessel_id,
        accountCode: row.accountCodes || row.account_codes || '',
        budgetDetails: row.description || '',
        baseBudget,
        totalBudget,
        totalExpenses,
        materialExpense: Number(row.materialExpense ?? row.material_expense ?? 0),
        logisticsExpense: Number(row.logisticsExpense ?? row.logistics_expense ?? 0),
        contributingPOs: row.contributingPOs || row.contributing_pos || [],
        remainingAmount,
        dateCreated: monthLabel,
        budgetMonth: row.budgetMonth || row.budget_month,
        isOneTime: !!(row.isOneTime || row.is_one_time),
        isRob: !!(row.isRob || row.is_rob),
        isFuture: !!(row.isFuture || row.is_future),
      }
    })
  }

  const loadBudgets = async () => {
    const params = new URLSearchParams()
    if (globalState.company) {
      params.append('companyId', globalState.company.id.toString())
    }
    if (globalState.vessel) {
      params.append('vesselId', globalState.vessel.id.toString())
    } else if (globalState.company && globalVessels.length > 0) {
      globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()))
    }

    const {data} = await api.get(`/accounts/budgets?${params.toString()}`)
    let budgets = Array.isArray(data) ? data : []

    if (globalState.vessel) {
      budgets = budgets.filter(
        (b: any) =>
          String(b.vesselId) === String(globalState.vessel!.id) ||
          String(b.vessel_id) === String(globalState.vessel!.id)
      )
    } else if (globalState.company && globalVessels.length > 0) {
      const vIds = new Set(globalVessels.map((v: any) => String(v.id)))
      budgets = budgets.filter(
        (b: any) => vIds.has(String(b.vesselId)) || vIds.has(String(b.vessel_id))
      )
    }

    setReports(buildReportsFromBudgets(budgets))
  }

  const loadAccountCodes = async (vesselId: any) => {
    const queryParams = new URLSearchParams()
    if (vesselId) queryParams.append('vesselId', vesselId)
    if (globalState.company?.id) queryParams.append('companyId', globalState.company.id.toString())

    if (isSuperAdmin && adminType && selectedAdminId) {
      if (adminType === 'ca') queryParams.append('caid', selectedAdminId)
      if (adminType === 'cga') queryParams.append('cgaid', selectedAdminId)
    }

    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
    const {data} = await api.get(`/accounts/budgets/account-codes${query}`)
    const rows = Array.isArray(data) ? data : []
    const uniqueCodes = Array.from(
      new Set(rows.map((row) => row.accountCodes || row.account_codes).filter(Boolean))
    ) as string[]
    setAvailableCodes(uniqueCodes)
  }

  const getLatestBudgetMonth = (vesselId: string): string | null => {
    const vesselBudgets = reports.filter(
      (r: any) => String(r.vesselId) === String(vesselId) && r.budgetMonth
    )
    if (vesselBudgets.length === 0) return null
    const months = vesselBudgets
      .map((r: any) => {
        const match = String(r.budgetMonth).match(/^(\d{4}-\d{2})/)
        return match ? match[1] : ''
      })
      .filter(Boolean)
    if (months.length === 0) return null
    return months.sort().pop() || null
  }

  const autoFillBudgetItems = (vesselId: string, month: string) => {
    if (editingBudget || !vesselId || !month) return
    const existingCodesForMonth = new Set(
      reports
        .filter(
          (r: any) =>
            String(r.vesselId) === String(vesselId) &&
            r.budgetMonth &&
            r.budgetMonth.startsWith(month)
        )
        .map((r: any) => r.accountCode)
    )
    const remainingCodes = availableCodes.filter((code: string) => !existingCodesForMonth.has(code))
    if (remainingCodes.length > 0) {
      setBudgetItems(
        remainingCodes.map((code: string) => ({
          id: Date.now() + Math.random(),
          accountCode: code,
          amount: '0',
          isOneTime: false,
        }))
      )
    } else {
      setBudgetItems([{id: Date.now(), accountCode: '', amount: '0', isOneTime: false}])
    }
  }

  const loadInitialData = async () => {
    setIsLoading(true)
    setLoadError('')
    try {
      const [vesselsData, adminsData, groupsData] = await Promise.all([
        getVesselList().catch(() => []),
        getCompanyList().catch(() => []),
        getCompanyAdminList().catch(() => []),
      ])

      // Scope vessels by role
      // Derive effective role type so custom roles (non-standard roleId) are
      // handled the same way as the base role they inherit from.
      const baseType = (
        (currentUser as any)?.role?.baseRoleType ||
        (currentUser as any)?.roleType ||
        (currentUser as any)?.role?.roleName ||
        ''
      ).toUpperCase()

      const effectiveRole =
        roleId === 1 || baseType === 'SUPER_ADMIN'          ? 'SUPER_ADMIN'
        : roleId === 4 || baseType === 'VESSEL_USER'        ? 'VESSEL_USER'
        : roleId === 5 || baseType === 'COMPANY_GROUP_ADMIN' ? 'COMPANY_GROUP_ADMIN'
        : roleId === 2 || baseType === 'COMPANY_ADMIN'      ? 'COMPANY_ADMIN'
        : roleId === 6 || baseType === 'OPERATOR'           ? 'OPERATOR'
        : baseType || 'UNKNOWN'

      let scopedVessels: any[] = []
      if (effectiveRole === 'VESSEL_USER') {
        const vId = currentUser?.vessel?.id
        scopedVessels = vId ? vesselsData.filter((v: any) => v.id === vId) : []
      } else {
        const operatorActsLikeSuperadmin = effectiveRole === 'OPERATOR' && !currentUser?.companyGroupAdminId
        const operatorActsLikeGroupAdmin = effectiveRole === 'OPERATOR' && !!currentUser?.companyGroupAdminId

        scopedVessels = vesselsData.filter((v: any) => {
          const isActive = v?.active ?? true
          if (effectiveRole === 'SUPER_ADMIN' || operatorActsLikeSuperadmin) return isActive
          if (effectiveRole === 'COMPANY_GROUP_ADMIN' || operatorActsLikeGroupAdmin) {
            const cgaId = currentUser?.companyGroupAdminId ?? (currentUser as any)?.cgaid ?? currentUser?.companyGroupAdmin?.id
            return isActive && (v?.companyGroupAdmin?.id === cgaId || v?.companyGroupAdminId === cgaId || v?.companyGroupId === cgaId)
          }
          if (effectiveRole === 'COMPANY_ADMIN') {
            const caId = currentUser?.companyAdminId ?? (currentUser as any)?.caid ?? currentUser?.companyAdmin?.id
            return isActive && (v?.companyAdmin?.id === caId || v?.companyAdminId === caId || v?.companyId === caId)
          }
          return false
        })
      }

      setVessels(scopedVessels)
      setCompanyAdmins(adminsData)
      setCompanyGroupAdmins(groupsData)
      await loadBudgets()
    } catch (err) {
      setLoadError(getErrorMessage(err, 'Failed to load initial data'))
    } finally {
      setIsLoading(false)
    }
  }

  // Stable key derived from vessel IDs — only changes when actual vessel list changes,
  // not on every context re-render (which would cause duplicate requests).
  const globalVesselIds = useMemo(
    () =>
      globalVessels
        .map((v: any) => v.id)
        .sort()
        .join(','),
    [globalVessels]
  )

  useEffect(() => {
    loadInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.company?.id, globalState.vessel?.id, globalVesselIds])

  useEffect(() => {
    ;(window as any).lucide?.createIcons()
  })

  useEffect(() => {
    loadAccountCodes(selectedVesselId || null).catch((err) => {
      setLoadError(getErrorMessage(err, 'Failed to load account codes'))
    })
  }, [selectedVesselId])

  const toggleVessel = (vesselName: string) => {
    setExpandedVessels((prev) => ({...prev, [vesselName]: !prev[vesselName]}))
  }

  const groupedReports: Record<string, any[]> = [...reports]
    .sort((a: any, b: any) => a.vesselName.localeCompare(b.vesselName))
    .reduce((acc: Record<string, any[]>, rep: any) => {
      if (!acc[rep.vesselName]) acc[rep.vesselName] = []
      acc[rep.vesselName].push(rep)
      return acc
    }, {})

  // Reset form when modal closes
  useEffect(() => {
    if (!isCreateModalOpen) {
      setAdminType('')
      setSelectedAdminId('')
      setSelectedVesselId('')
      setBudgetItems([{id: Date.now(), accountCode: '', amount: '', isOneTime: false}])
      setBudgetDetails('')
      setBudgetMonth('')
      setIsCopyModalOpen(false)
      setEditingBudget(null)
    }
  }, [isCreateModalOpen])

  // Click outside to close dropdowns
  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null)
      setDropdownPos(null)
    }
    document.addEventListener('click', closeDropdowns)
    return () => document.removeEventListener('click', closeDropdowns)
  }, [])

  // Pre-fill form when editing a budget
  useEffect(() => {
    if (editingBudget) {
      const vessel = vessels.find(
        (v: any) =>
          v.id === editingBudget.vesselId ||
          String(v.fleetName || v.fleet_name || '').toLowerCase() ===
            String(editingBudget.vesselName || '').toLowerCase()
      )
      if (vessel) {
        setSelectedVesselId(String(vessel.id))
        if (isSuperAdmin) {
          if (vessel.companyAdminId || vessel.companyId || vessel.companyAdmin?.id) {
            setAdminType('ca')
            setSelectedAdminId(
              String(vessel.companyAdminId || vessel.companyId || vessel.companyAdmin?.id)
            )
          } else if (
            vessel.companyGroupAdminId ||
            vessel.companyGroupId ||
            vessel.companyGroupAdmin?.id
          ) {
            setAdminType('cga')
            setSelectedAdminId(
              String(
                vessel.companyGroupAdminId || vessel.companyGroupId || vessel.companyGroupAdmin?.id
              )
            )
          }
        }
      } else {
        setSelectedVesselId(String(editingBudget.vesselId || ''))
      }

      if (editingBudget.budgetMonth) {
        const match = editingBudget.budgetMonth.match(/^(\d{4}-\d{2})/)
        setBudgetMonth(match ? match[1] : '')
      } else {
        setBudgetMonth('')
      }

      setBudgetDetails(editingBudget.budgetDetails || '')
      setBudgetItems([
        {
          id: Date.now(),
          accountCode: editingBudget.accountCode || '',
          amount: String(editingBudget.totalBudget || ''),
          isOneTime: Boolean(
            editingBudget.isOneTime ||
              editingBudget.is_one_time ||
              editingBudget.oneTimeBudget ||
              editingBudget.isOneTimeBudget
          ),
        },
      ])
    }
  }, [editingBudget, vessels, isSuperAdmin])

  useEffect(() => {
    if (isCreateModalOpen && !editingBudget && selectedVesselId && budgetMonth && availableCodes) {
      autoFillBudgetItems(selectedVesselId, budgetMonth)
    }
  }, [availableCodes])

  const handleVesselChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value
    setSelectedVesselId(val)
    // Removed synchronous autoFillBudgetItems(val, budgetMonth) because availableCodes needs to load first
  }

  const toggleBudget = async (rawId: number, budgetId: string) => {
    setExpandedBudgets((prev) => ({...prev, [budgetId]: !prev[budgetId]}))

    if (!budgetTimelines[budgetId]) {
      try {
        setTimelineLoading((prev) => ({...prev, [budgetId]: true}))
        const res = await api.get(`/accounts/budgets/${rawId}/timeline`)
        setBudgetTimelines((prev) => ({...prev, [budgetId]: res.data}))
      } catch (err) {
        console.error('Failed to load timeline', err)
      } finally {
        setTimelineLoading((prev) => ({...prev, [budgetId]: false}))
      }
    }
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (!editingBudget && selectedVesselId && val) {
      const latestMonth = getLatestBudgetMonth(selectedVesselId)
      if (latestMonth && val < latestMonth) {
        alert('Please delete newer budget before creating for an older date.')
        return
      }
    }
    setBudgetMonth(val)
    if (selectedVesselId && val) {
      autoFillBudgetItems(selectedVesselId, val)
    }
  }

  const addBudgetItem = () =>
    setBudgetItems([
      ...budgetItems,
      {id: Date.now() + Math.random(), accountCode: '', amount: '', isOneTime: false},
    ])
  const removeBudgetItem = (id: any) => setBudgetItems(budgetItems.filter((item) => item.id !== id))
  const updateBudgetItem = (id: any, field: string, value: any) =>
    setBudgetItems(budgetItems.map((item) => (item.id === id ? {...item, [field]: value} : item)))

  const handleCopyBudget = (vesselName: string) => {
    const vesselReports = groupedReports[vesselName]
    if (vesselReports) {
      const copiedItems = vesselReports.map((rep) => ({
        id: Date.now() + Math.random(),
        accountCode: rep.accountCode,
        amount: rep.totalBudget.toString(),
        isOneTime: Boolean(
          rep.isOneTime || rep.is_one_time || rep.oneTimeBudget || rep.isOneTimeBudget
        ),
      }))
      setBudgetItems(copiedItems)
    }
    setIsCopyModalOpen(false)
  }

  const handleDelete = async (e: React.MouseEvent, rawId: number) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this budget item?')) return
    try {
      await api.delete(`/accounts/budgets/${rawId}`)
      await loadBudgets()
    } catch (err) {
      console.error('Failed to delete budget', err)
      alert(getErrorMessage(err, 'Failed to delete budget item'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validItems = budgetItems.filter(
      (item) => item.accountCode && item.amount && Number(item.amount) > 0
    )
    if (validItems.length === 0 || !selectedVesselId || !budgetMonth) return

    if (editingBudget) {
      const payload = {
        vesselId: Number(selectedVesselId),
        budgetMonth: budgetMonth.includes('-01') ? budgetMonth : `${budgetMonth}-01`,
        description: budgetDetails,
        accountCode: validItems[0].accountCode,
        accountCodes: validItems[0].accountCode,
        budget: Number(validItems[0].amount),
        totalBudget: Number(validItems[0].amount),
        isOneTime: !!validItems[0].isOneTime,
        oneTimeBudget: !!validItems[0].isOneTime,
        isOneTimeBudget: !!validItems[0].isOneTime,
      }

      try {
        await api.put(`/accounts/budgets/${editingBudget.rawId}`, payload)
        await loadBudgets()
        setIsCreateModalOpen(false)
      } catch (err) {
        setLoadError(getErrorMessage(err, 'Failed to update budget'))
      }
    } else {
      const duplicateItems = validItems.filter((item) =>
        reports.some(
          (r: any) =>
            String(r.vesselId) === String(selectedVesselId) &&
            r.budgetMonth &&
            r.budgetMonth.startsWith(budgetMonth) &&
            r.accountCode === item.accountCode
        )
      )

      if (duplicateItems.length > 0) {
        const codes = duplicateItems.map((i) => i.accountCode).join(', ')
        const matchingBudget = reports.find(
          (r: any) =>
            String(r.vesselId) === String(selectedVesselId) &&
            r.budgetMonth &&
            r.budgetMonth.startsWith(budgetMonth) &&
            r.accountCode === duplicateItems[0].accountCode
        )
        if (
          window.confirm(
            `Budget already exists for account code(s): ${codes}. Would you like to edit that?`
          )
        ) {
          if (matchingBudget) setEditingBudget(matchingBudget)
        }
        return
      }

      const payload = {
        vesselId: Number(selectedVesselId),
        budgetMonth: `${budgetMonth}-01`,
        description: budgetDetails,
        items: validItems.map((item) => ({
          accountCode: item.accountCode,
          budget: Number(item.amount),
          isOneTime: !!item.isOneTime,
          oneTimeBudget: !!item.isOneTime,
          isOneTimeBudget: !!item.isOneTime,
        })),
      }

      try {
        await api.post('/accounts/budgets', payload)
        await loadBudgets()
        setIsCreateModalOpen(false)
      } catch (err) {
        setLoadError(getErrorMessage(err, 'Failed to create budget'))
      }
    }
  }

  const formatCurrency = (val: any) => {
    const safeValue = Number.isFinite(Number(val)) ? Number(val) : 0
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(safeValue)
  }

  return (
    <>
      <div className='flex h-screen w-full overflow-hidden bg-slate-50'>
        <div id='sidebar-overlay' className='fixed inset-0 bg-black/50 z-40 hidden md:hidden'></div>

        <main className='flex-1 flex flex-col h-full overflow-hidden relative'>
          <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6' id='main-scroll'>
            {loadError && (
              <div className='mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
                {loadError}
              </div>
            )}

            {isLoading && (
              <div className='mb-5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500'>
                Loading budgets...
              </div>
            )}

            <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6'>
              <div>
                <div className='text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1'>
                  Accounts / Vessel Expense
                </div>
                <h1 className='text-2xl font-bold text-slate-900 leading-tight'>Budgets </h1>
                <p className='text-sm text-slate-500'>Allocate budgets and track vessel expenses</p>
              </div>
              {hasPermission(ACCOUNTS_PERMS.BUDGET_CREATE) && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className='px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2'
                >
                  <Plus className='w-4 h-4' /> Create Budget
                </button>
              )}
            </div>

            {/* Table */}
            <div className='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden'>
              <div className='overflow-x-auto'>
                <table className='w-full text-left min-w-225'>
                  <thead>
                    <tr className='bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-bold text-slate-400 tracking-wider'>
                      <th className='px-5 py-3'>Budget No.</th>
                      <th className='px-5 py-3'>Vessel</th>
                      <th className='px-5 py-3'>Account Code</th>
                      <th className='px-5 py-3 text-right'>Budget Allocated</th>
                      <th className='px-5 py-3 text-right'>Total Exp.</th>
                      <th className='px-5 py-3 text-right'>Remaining</th>
                      <th className='px-5 py-3 text-center'>Status</th>
                      <th className='px-5 py-3'></th>
                    </tr>
                  </thead>
                  <tbody className='text-sm divide-y divide-slate-100'>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan={8} className='px-5 py-12 text-center'>
                          <div className='flex flex-col items-center justify-center'>
                            <div className='w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-4'>
                              <Wallet className='w-8 h-8' />
                            </div>
                            <h3 className='text-lg font-semibold text-slate-900 mb-1'>
                              No budget reports found
                            </h3>
                            <p className='text-slate-500 text-sm'>
                              Create your first budget expense to see it here.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      Object.entries(groupedReports).map(([vesselName, vesselReports]) => {
                        const isExpanded = expandedVessels[vesselName]

                        const activeVesselReports = vesselReports.filter((r) => !r.isFuture)
                        const reportsForCalc =
                          activeVesselReports.length > 0 ? activeVesselReports : vesselReports

                        const latestReportsByCode = new Map<string, any>()
                        reportsForCalc.forEach((r) => {
                          const existing = latestReportsByCode.get(r.accountCode)
                          if (
                            !existing ||
                            (r.budgetMonth &&
                              existing.budgetMonth &&
                              r.budgetMonth > existing.budgetMonth)
                          ) {
                            latestReportsByCode.set(r.accountCode, r)
                          }
                        })
                        const latestReports = Array.from(latestReportsByCode.values())

                        const totalBudget = reportsForCalc.reduce((sum, r) => sum + r.baseBudget, 0)
                        const totalExpenses = reportsForCalc.reduce(
                          (sum, r) => sum + r.totalExpenses,
                          0
                        )
                        const remainingAmount = latestReports.reduce(
                          (sum, r) => sum + r.remainingAmount,
                          0
                        )

                        let usagePercent = totalBudget > 0 ? (totalExpenses / totalBudget) * 100 : 0
                        if (totalBudget <= 0 && totalExpenses > 0) usagePercent = 100
                        const cappedUsage = Math.min(usagePercent, 100)
                        let statusTheme = 'bg-emerald-100 text-emerald-700'
                        let statusText = 'Healthy'
                        let barTheme = 'from-emerald-400 to-emerald-600'

                        if (remainingAmount < 0 || usagePercent > 90) {
                          statusTheme = 'bg-red-100 text-red-700'
                          statusText = 'Critical'
                          barTheme = 'from-red-400 to-red-600'
                        } else if (usagePercent > 75) {
                          statusTheme = 'bg-amber-100 text-amber-700'
                          statusText = 'Warning'
                          barTheme = 'from-amber-400 to-amber-600'
                        }

                        // Sort vessel reports so current/past appear first, future budgets appear down/at the bottom
                        const sortedVesselReports = [...vesselReports].sort((a, b) => {
                          const aFuture = a.isFuture ? 1 : 0
                          const bFuture = b.isFuture ? 1 : 0
                          if (aFuture !== bFuture) return aFuture - bFuture
                          const aMonth = a.budgetMonth || ''
                          const bMonth = b.budgetMonth || ''
                          return bMonth.localeCompare(aMonth)
                        })

                        return (
                          <React.Fragment key={vesselName}>
                            {/* Vessel Group Header */}
                            <tr
                              className='bg-white hover:bg-slate-50 transition-colors cursor-pointer border-b-2 border-slate-100 group'
                              onClick={() => toggleVessel(vesselName)}
                            >
                              <td className='px-5 py-4' colSpan={8}>
                                <div className='rounded-2xl border border-slate-200 bg-gradient-to-r from-white via-slate-50 to-blue-50/60 p-4 shadow-sm transition-all group-hover:shadow-md'>
                                  <div className='flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between'>
                                    <div className='flex items-center gap-4'>
                                      <div className='w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:border-blue-200 transition-colors'>
                                        {isExpanded ? (
                                          <ChevronDown className='w-4 h-4' />
                                        ) : (
                                          <ChevronRight className='w-4 h-4' />
                                        )}
                                      </div>
                                      <div>
                                        <div className='text-[10px] uppercase tracking-wider text-slate-400 font-bold'>
                                          Vessel
                                        </div>
                                        <div className='text-lg font-bold text-slate-900'>
                                          {vesselName}
                                        </div>
                                        <div className='flex flex-wrap items-center gap-2 mt-1'>
                                          <span className='text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-bold'>
                                            {vesselReports.length} Budgets
                                          </span>
                                          {vesselReports.some((r) => r.isFuture) && (
                                            <span className='text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold border border-amber-200'>
                                              {vesselReports.filter((r) => r.isFuture).length} Future
                                            </span>
                                          )}
                                          <span className='text-[10px] bg-white/80 text-slate-600 px-2 py-0.5 rounded-full font-semibold border border-slate-200'>
                                            {cappedUsage.toFixed(1)}% Used
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-80'>
                                      <div className='rounded-xl bg-white/80 border border-slate-200 px-3 py-2 text-right'>
                                        <div className='text-[10px] uppercase tracking-wider text-slate-400 font-bold'>
                                          Budget
                                        </div>
                                        <div className='text-base font-bold text-slate-900'>
                                          {formatCurrency(totalBudget)}
                                        </div>
                                      </div>
                                      <div className='rounded-xl bg-white/80 border border-rose-200 px-3 py-2 text-right'>
                                        <div className='text-[10px] uppercase tracking-wider text-rose-400 font-bold'>
                                          Expenses
                                        </div>
                                        <div className='text-base font-bold text-rose-600'>
                                          {formatCurrency(totalExpenses)}
                                        </div>
                                      </div>
                                      <div
                                        className={`rounded-xl bg-white/80 border ${
                                          remainingAmount < 0
                                            ? 'border-red-200'
                                            : 'border-emerald-200'
                                        } px-3 py-2 text-right`}
                                      >
                                        <div
                                          className={`text-[10px] uppercase tracking-wider font-bold ${
                                            remainingAmount < 0
                                              ? 'text-red-500'
                                              : 'text-emerald-500'
                                          }`}
                                        >
                                          Remaining
                                        </div>
                                        <div
                                          className={`text-base font-bold ${
                                            remainingAmount < 0
                                              ? 'text-red-600'
                                              : 'text-emerald-600'
                                          }`}
                                        >
                                          {formatCurrency(remainingAmount)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className='flex flex-col items-end gap-2 min-w-45'>
                                      <span
                                        className={`px-3 py-1 rounded-full text-[10px] font-bold ${statusTheme}`}
                                      >
                                        {statusText}
                                      </span>
                                      <div className='w-full h-2 rounded-full bg-slate-200 overflow-hidden'>
                                        <div
                                          className={`h-full bg-gradient-to-r ${barTheme}`}
                                          style={{width: `${cappedUsage}%`}}
                                        ></div>
                                      </div>
                                      <div className='text-[10px] text-slate-500'>
                                        {cappedUsage.toFixed(1)}% of budget used
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>

                            {/* Expanded Rows */}
                            {isExpanded &&
                              sortedVesselReports.map((rep) => {
                                let repUsagePercent =
                                  rep.totalBudget > 0
                                    ? (rep.totalExpenses / rep.totalBudget) * 100
                                    : 0
                                if (rep.totalBudget <= 0 && rep.totalExpenses > 0)
                                  repUsagePercent = 100
                                const repCappedUsage = Math.min(repUsagePercent, 100)
                                let repStatusTheme =
                                  'bg-emerald-50 text-emerald-600 border border-emerald-200'
                                let repBarTheme = 'from-emerald-400 to-emerald-600'

                                if (rep.remainingAmount < 0 || repUsagePercent > 90) {
                                  repStatusTheme = 'bg-red-50 text-red-600 border border-red-200'
                                  repBarTheme = 'from-red-400 to-red-600'
                                } else if (repUsagePercent > 75) {
                                  repStatusTheme =
                                    'bg-amber-50 text-amber-600 border border-amber-200'
                                  repBarTheme = 'from-amber-400 to-amber-600'
                                }

                                return (
                                  <React.Fragment key={rep.id}>
                                    <tr
                                      className={`bg-white transition-colors border-b border-slate-100 ${
                                        rep.isOneTime || rep.isFuture ? '' : 'cursor-pointer hover:bg-slate-50'
                                      } ${
                                        expandedBudgets[rep.id] && !rep.isOneTime && !rep.isFuture
                                          ? 'bg-slate-50 border-l-4 border-l-blue-400'
                                          : ''
                                      } ${rep.isFuture ? 'bg-amber-50/20' : ''}`}
                                      onClick={() =>
                                        !rep.isOneTime && !rep.isFuture && toggleBudget(rep.rawId, rep.id)
                                      }
                                    >
                                      <td className='px-5 py-3 pl-12'>
                                        <div className='font-bold text-slate-800 text-sm flex items-center gap-2'>
                                          {rep.isOneTime || rep.isFuture ? (
                                            <div className='w-4 h-4 shrink-0' />
                                          ) : expandedBudgets[rep.id] ? (
                                            <ChevronDown className='w-4 h-4 text-slate-400 shrink-0' />
                                          ) : (
                                            <ChevronRight className='w-4 h-4 text-slate-400 shrink-0' />
                                          )}
                                          {rep.id}
                                          {rep.isOneTime && (
                                            <span className='px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200'>
                                              One Time
                                            </span>
                                          )}
                                          {rep.isFuture && (
                                            <span className='px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200'>
                                              Future
                                            </span>
                                          )}
                                        </div>
                                        <div className='text-[10px] text-slate-400 font-mono mt-0.5 ml-6'>
                                          {rep.dateCreated}
                                        </div>
                                      </td>
                                      <td className='px-5 py-3 text-slate-500 text-[11px]'>
                                        {isSuperAdmin ? `Org: ${rep.adminName}` : ''}
                                      </td>
                                      <td className='px-5 py-3'>
                                        <div className='text-sm text-slate-800 font-medium'>
                                          {rep.accountCode}
                                        </div>
                                      </td>
                                      <td className='px-5 py-3 text-right font-semibold text-slate-700'>
                                        {formatCurrency(rep.baseBudget)}
                                      </td>
                                      <td className='px-5 py-3 text-right'>
                                        <div className='font-bold text-red-500 flex items-center justify-end gap-1'>
                                          {rep.isRob && (
                                            <span className='px-1.5 py-0.5 bg-rose-100 text-rose-700 text-[9px] rounded-full font-bold uppercase mr-1'>
                                              ROB
                                            </span>
                                          )}
                                          {formatCurrency(rep.totalExpenses)}
                                        </div>
                                      </td>
                                      <td
                                        className={`px-5 py-3 text-right font-semibold ${
                                          rep.remainingAmount < 0
                                            ? 'text-red-600'
                                            : 'text-emerald-600'
                                        }`}
                                      >
                                        {formatCurrency(rep.remainingAmount)}
                                      </td>
                                      <td className='px-5 py-3 text-center'>
                                        {rep.isFuture ? (
                                          <span className='px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200'>
                                            Upcoming
                                          </span>
                                        ) : rep.isOneTime ? (
                                          <span className='text-slate-300 text-xs font-semibold'>
                                            -
                                          </span>
                                        ) : (
                                          <div className='flex flex-col items-center gap-1'>
                                            <div
                                              className={`w-2 h-2 rounded-full ${
                                                rep.remainingAmount < 0 || repUsagePercent > 90
                                                  ? 'bg-red-500'
                                                  : repUsagePercent > 75
                                                  ? 'bg-amber-500'
                                                  : 'bg-emerald-500'
                                              }`}
                                            ></div>
                                            <div className='w-14 h-1.5 rounded-full bg-slate-200 overflow-hidden'>
                                              <div
                                                className={`h-full bg-gradient-to-r ${repBarTheme}`}
                                                style={{width: `${repCappedUsage}%`}}
                                              ></div>
                                            </div>
                                          </div>
                                        )}
                                      </td>
                                      <td
                                        className='px-5 py-3 text-right relative'
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {(hasPermission(ACCOUNTS_PERMS.BUDGET_UPDATE) ||
                                          hasPermission(ACCOUNTS_PERMS.BUDGET_DELETE)) && (
                                          <button
                                            className='p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              if (activeDropdown === rep.id) {
                                                setActiveDropdown(null)
                                                setDropdownPos(null)
                                              } else {
                                                setDropdownPos(
                                                  getDropdownPosition(
                                                    e.currentTarget as HTMLElement
                                                  )
                                                )
                                                setActiveDropdown(rep.id)
                                              }
                                            }}
                                          >
                                            <MoreHorizontal className='w-4 h-4' />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                    {expandedBudgets[rep.id] && !rep.isOneTime && (
                                      <tr className='bg-slate-50/50 border-b border-slate-200'>
                                        <td colSpan={8} className='p-0'>
                                          <div className='px-8 py-5 border-l-4 border-transparent bg-slate-50 shadow-inner'>
                                            {/* ---- Regular Budget: Timeline Projection ---- */}
                                            <div className='flex items-center justify-between mb-4'>
                                              <h4 className='text-sm font-bold text-slate-700'>
                                                Timeline Projection
                                              </h4>
                                            </div>
                                            {timelineLoading[rep.id] ? (
                                              <div className='py-8 text-center text-slate-500'>
                                                Loading timeline...
                                              </div>
                                            ) : (
                                              <div className='rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm'>
                                                <table className='w-full text-left text-xs'>
                                                  <thead className='bg-slate-50/80 text-slate-500 uppercase font-bold tracking-wider border-b border-slate-200'>
                                                    <tr>
                                                      <th className='px-4 py-3'>Month</th>
                                                      <th className='px-4 py-3'>Type</th>
                                                      <th className='px-4 py-3 text-right'>
                                                        Base Budget
                                                      </th>
                                                      <th className='px-4 py-3 text-right'>
                                                        Carry Over
                                                      </th>
                                                      <th className='px-4 py-3 text-right'>
                                                        Total Budget
                                                      </th>
                                                      <th className='px-4 py-3 text-right'>
                                                        Total Exp.
                                                      </th>
                                                      <th className='px-4 py-3 text-right'>
                                                        Variance
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className='divide-y divide-slate-100'>
                                                    {budgetTimelines[rep.id]?.map(
                                                      (tl: any, i: number) => (
                                                        <tr
                                                          key={i}
                                                          className='hover:bg-slate-50/80 transition-colors'
                                                        >
                                                          <td className='px-4 py-2.5 font-medium text-slate-700'>
                                                            <div
                                                              className='cursor-pointer text-blue-600 hover:underline inline-block font-medium'
                                                              onClick={(e) => {
                                                                e.stopPropagation()
                                                                setSelectedPos(
                                                                  tl.contributingPOs || []
                                                                )
                                                                setSelectedPoAccount(
                                                                  `${
                                                                    rep.accountCode
                                                                  } - ${formatBudgetMonth(
                                                                    tl.month
                                                                  )}`
                                                                )
                                                                setIsPoModalOpen(true)
                                                              }}
                                                            >
                                                              {formatBudgetMonth(tl.month)}
                                                            </div>
                                                          </td>
                                                          <td className='px-4 py-2.5'>
                                                            <span
                                                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                                tl.budgetType === 'Explicit'
                                                                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                                                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                                                              }`}
                                                            >
                                                              {tl.budgetType}
                                                            </span>
                                                          </td>
                                                          <td className='px-4 py-2.5 text-right font-medium text-slate-600'>
                                                            {formatCurrency(tl.baseBudget)}
                                                          </td>
                                                          <td className='px-4 py-2.5 text-right font-medium text-slate-500'>
                                                            {formatCurrency(tl.previousVariance)}
                                                          </td>
                                                          <td className='px-4 py-2.5 text-right font-bold text-slate-800'>
                                                            {formatCurrency(tl.effectiveBudget)}
                                                          </td>
                                                          <td className='px-4 py-2.5 text-right'>
                                                            <div className='font-bold text-rose-600 flex items-center justify-end gap-1'>
                                                              {tl.isRob && (
                                                                <span className='px-1 py-0.5 bg-rose-100 text-rose-700 text-[8px] rounded-full font-bold uppercase'>
                                                                  ROB
                                                                </span>
                                                              )}
                                                              {formatCurrency(tl.expenses)}
                                                            </div>
                                                            {((tl.materialExpense ?? 0) > 0 ||
                                                              (tl.logisticsExpense ?? 0) > 0) && (
                                                              <div className='flex flex-col items-end gap-0.5 mt-0.5'></div>
                                                            )}
                                                          </td>
                                                          <td
                                                            className={`px-4 py-2.5 text-right font-bold ${
                                                              tl.currentVariance < 0
                                                                ? 'text-rose-600'
                                                                : 'text-emerald-600'
                                                            }`}
                                                          >
                                                            {formatCurrency(tl.currentVariance)}
                                                          </td>
                                                        </tr>
                                                      )
                                                    )}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                )
                              })}
                          </React.Fragment>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modal */}
      {isCreateModalOpen && (
        <div className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm'>
          <div className='bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]'>
            <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center'>
                  <Wallet className='w-5 h-5 text-blue-600' />
                </div>
                <div>
                  <h2 className='text-lg font-bold text-slate-900 leading-tight'>
                    {editingBudget ? 'Edit Budget' : 'Create Budget'}
                  </h2>
                  <p className='text-xs text-slate-500'>
                    {editingBudget
                      ? 'Update budget allocation details'
                      : 'Allocate new budget for a vessel'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className='p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            <div className='flex-1 overflow-y-auto p-6'>
              <form id='budgetForm' onSubmit={handleSubmit} className='space-y-5'>
                {isSuperAdmin && !editingBudget && (
                  <div className='p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-4'>
                    <div>
                      <label className='block text-xs font-semibold text-slate-700 mb-1.5'>
                        Select Organization Type *
                      </label>
                      <SearchableSelect
                        className='w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all'
                        value={adminType}
                        onChange={(e) => {
                          setAdminType(e.target.value)
                          setSelectedAdminId('')
                        }}
                      >
                        <option value=''>-- Choose Type --</option>
                        <option value='ca'>Company Admin</option>
                        <option value='cga'>Company Group Admin</option>
                      </SearchableSelect>
                    </div>

                    {adminType && (
                      <div className='animate-in fade-in slide-in-from-top-2 duration-300'>
                        <label className='block text-xs font-semibold text-slate-700 mb-1.5'>
                          Select Organization *
                        </label>
                        <SearchableSelect
                          className='w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all'
                          value={selectedAdminId}
                          onChange={(e) => setSelectedAdminId(e.target.value)}
                        >
                          <option value=''>-- Select Organization --</option>
                          {(adminType === 'ca' ? companyAdmins : companyGroupAdmins).map(
                            (admin) => (
                              <option key={admin.id} value={admin.id}>
                                {admin.name ||
                                  admin.companyAdminName ||
                                  admin.companyGroupAdminName ||
                                  `Org #${admin.id}`}
                              </option>
                            )
                          )}
                        </SearchableSelect>
                      </div>
                    )}
                  </div>
                )}

                {!editingBudget && (
                  <div className='p-4 rounded-xl bg-slate-50 border border-slate-200'>
                    <label className='block text-xs font-semibold text-slate-700 mb-1.5'>
                      Select Vessel *
                    </label>
                    <SearchableSelect
                      required
                      className='w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all'
                      value={selectedVesselId}
                      onChange={handleVesselChange}
                    >
                      <option value=''>-- Choose Vessel --</option>
                      {filteredVessels.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.fleetName || v.fleet_name || v.imoNumber || v.imo_number || v.id}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                <div className='p-4 rounded-xl bg-slate-50 border border-slate-200'>
                  <label className='block text-xs font-semibold text-slate-700 mb-1.5'>
                    Budget Month *
                  </label>
                  <input
                    type='month'
                    required
                    className='w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all'
                    value={budgetMonth}
                    onChange={handleMonthChange}
                  />
                </div>

                {/* Item Table UI for Budgets */}
                <div className='bg-white border border-slate-200 rounded-xl overflow-visible shadow-sm'>
                  <div className='bg-slate-50 border-b border-slate-200 px-4 py-3 rounded-t-xl'>
                    <h3 className='text-sm font-bold text-slate-800'>Budget Items</h3>
                  </div>
                  <div className='overflow-x-visible'>
                    <table className='w-full text-left border-collapse'>
                      <thead>
                        <tr className='text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200'>
                          <th className='px-3 py-3 w-8 text-center bg-white'></th>
                          <th className='px-3 py-3 w-[42%] bg-white'>ACCOUNT CODE *</th>
                          <th className='px-3 py-3 w-[53%] bg-white'>BUDGET AMOUNT (USD) *</th>
                          <th className='px-3 py-3 w-10 text-center bg-white'></th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-slate-100'>
                        {budgetItems.map((item) => (
                          <tr key={item.id} className='hover:bg-slate-50 transition-colors group'>
                            <td className='px-3 py-3 text-slate-300 align-middle text-center'>
                              <GripVertical className='w-4 h-4 mx-auto' />
                            </td>
                            <td className='px-3 py-3 align-middle'>
                              <SearchableSelect
                                required
                                className='w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all'
                                value={item.accountCode}
                                onChange={(e) =>
                                  updateBudgetItem(item.id, 'accountCode', e.target.value)
                                }
                              >
                                <option value=''>Type or click to select an item.</option>
                                {availableCodes.map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </SearchableSelect>
                            </td>
                            <td className='px-3 py-3 align-middle'>
                              <div className='flex items-center gap-3'>
                                <div className='relative flex-1 min-w-[110px]'>
                                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                                    <span className='text-slate-400 font-medium text-sm'>$</span>
                                  </div>
                                  <input
                                    type='number'
                                    required
                                    min='0'
                                    step='0.01'
                                    className='w-full pl-8 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-mono text-right'
                                    placeholder='0.00'
                                    value={item.amount}
                                    onChange={(e) =>
                                      updateBudgetItem(item.id, 'amount', e.target.value)
                                    }
                                  />
                                </div>
                                <label className='inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 cursor-pointer whitespace-nowrap select-none shrink-0 py-1.5 px-2 rounded-lg hover:bg-slate-100/80 transition-all'>
                                  <input
                                    type='checkbox'
                                    checked={!!item.isOneTime}
                                    onChange={(e) =>
                                      updateBudgetItem(item.id, 'isOneTime', e.target.checked)
                                    }
                                    className='w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-2 focus:ring-blue-500 cursor-pointer accent-blue-600'
                                  />
                                  <span>One Time Budget</span>
                                </label>
                              </div>
                            </td>
                            <td className='px-3 py-3 text-center align-middle'>
                              {!editingBudget && (
                                <button
                                  type='button'
                                  onClick={() => removeBudgetItem(item.id)}
                                  className='text-slate-400 hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50'
                                >
                                  <Trash2 className='w-4 h-4 mx-auto' />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!editingBudget && (
                    <div className='p-4 border-t border-slate-200 bg-white rounded-b-xl'>
                      <button
                        type='button'
                        onClick={addBudgetItem}
                        className='flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-semibold hover:bg-blue-100 transition-colors'
                      >
                        <Plus className='w-4 h-4' />
                        Add New Row
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <div className='flex justify-between items-center mb-1.5'>
                    <label className='block text-xs font-semibold text-slate-700'>
                      Description / Details
                    </label>
                    {!editingBudget && (
                      <button
                        type='button'
                        onClick={() => setIsCopyModalOpen(true)}
                        className='flex items-center justify-center px-5 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-semibold hover:bg-blue-100 transition-colors border border-blue-200'
                      >
                        Copy Budget
                      </button>
                    )}
                  </div>
                  <textarea
                    className='w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none'
                    rows={3}
                    placeholder='Enter details about this budget allocation...'
                    value={budgetDetails}
                    onChange={(e) => setBudgetDetails(e.target.value)}
                  ></textarea>
                </div>
              </form>
            </div>
            <div className='px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3'>
              <button
                type='button'
                onClick={() => setIsCreateModalOpen(false)}
                className='px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors'
              >
                Cancel
              </button>
              <button
                form='budgetForm'
                type='submit'
                disabled={
                  !budgetItems.some((item) => item.accountCode && item.amount) ||
                  !selectedVesselId ||
                  !budgetMonth
                }
                className='px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {editingBudget ? 'Update Budget' : 'Submit Budget'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PO Details Modal */}
      {isPoModalOpen && (
        <div className='fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm'>
          <div className='bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200'>
            <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center'>
                  <Wallet className='w-5 h-5 text-blue-600' />
                </div>
                <div>
                  <h2 className='text-lg font-bold text-slate-900 leading-tight'>
                    Purchase Orders
                  </h2>
                  <p className='text-xs text-slate-500'>{selectedPoAccount}</p>
                </div>
              </div>
              <button
                onClick={() => setIsPoModalOpen(false)}
                className='p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            <div className='p-6 max-h-[60vh] overflow-y-auto bg-slate-50'>
              {selectedPos.length === 0 ? (
                <div className='text-center py-8 text-slate-500'>
                  <p>No purchase orders found for this budget.</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  {selectedPos.map((po, idx) => (
                    <div
                      key={idx}
                      className='bg-white rounded-xl p-4 border border-slate-200 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow'
                    >
                      <div className='flex justify-between items-start'>
                        <div>
                          <div className='flex items-center gap-2 mb-1'>
                            <div className='font-bold text-slate-800 text-base'>{po.poNumber}</div>
                            {po.status && (
                              <span className='bg-indigo-50 text-indigo-600 border border-indigo-200 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider'>
                                {po.status}
                              </span>
                            )}
                          </div>
                          <div className='font-semibold text-slate-600 text-sm'>
                            {po.vendorName || 'Unknown Vendor'}
                          </div>
                        </div>
                        <div className='text-right'>
                          <div className='text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5'>
                            Expense Contribution
                          </div>
                          <div className='font-bold text-rose-600 text-lg'>
                            {formatCurrency(po.expenseAssigned || po.totalAmount)}
                          </div>
                        </div>
                      </div>

                      {po.description && (
                        <div className='text-sm text-slate-500 italic bg-slate-50 p-2 rounded-lg border border-slate-100'>
                          "{po.description}"
                        </div>
                      )}

                      <div className='flex items-center justify-between pt-3 border-t border-slate-100 mt-1'>
                        <div className='flex flex-wrap items-center gap-3 text-xs'>
                          <span className='bg-slate-100 text-slate-600 px-2 py-1 rounded font-semibold'>
                            {po.type}
                          </span>
                          {po.date && (
                            <span className='text-slate-500 font-medium'>
                              Date: {new Date(po.date).toLocaleDateString()}
                            </span>
                          )}
                          {po.paymentStatus && (
                            <span
                              className={`px-2 py-1 rounded font-semibold ${
                                po.paymentStatus === 'PAID'
                                  ? 'bg-emerald-50 text-emerald-600'
                                  : 'bg-amber-50 text-amber-600'
                              }`}
                            >
                              Payment: {po.paymentStatus}
                            </span>
                          )}
                          {po.deliveryLocation && (
                            <span className='text-slate-500 font-medium'>
                              Location: {po.deliveryLocation}
                            </span>
                          )}
                          {po.raisedBy && (
                            <span className='text-slate-500 font-medium'>
                              Raised By: {po.raisedBy}
                            </span>
                          )}
                        </div>
                        <div className='text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-1 rounded whitespace-nowrap ml-4'>
                          PO Total: {po.currency || 'USD'}{' '}
                          {po.totalAmount != null
                            ? formatCurrency(po.totalAmount).replace('$', '')
                            : 'N/A'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-end'>
              <button
                onClick={() => setIsPoModalOpen(false)}
                className='px-4 py-2 bg-slate-100 border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Budget Modal */}
      {isCopyModalOpen && (
        <div className='fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm'>
          <div className='bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200'>
            <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center'>
                  <Copy className='w-5 h-5 text-blue-600' />
                </div>
                <div>
                  <h2 className='text-lg font-bold text-slate-900 leading-tight'>
                    Copy Existing Budget
                  </h2>
                  <p className='text-xs text-slate-500'>Select a vessel to copy its budget items</p>
                </div>
              </div>
              <button
                onClick={() => setIsCopyModalOpen(false)}
                className='p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            <div className='p-6 max-h-[60vh] overflow-y-auto'>
              {Object.keys(groupedReports).length === 0 ? (
                <div className='text-center py-8 text-slate-500'>
                  <p>No existing budgets found to copy.</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  {Object.entries(groupedReports).map(([vesselName, vesselReports]) => {
                    const totalBudget = vesselReports.reduce((sum, r) => sum + r.totalBudget, 0)
                    return (
                      <div
                        key={vesselName}
                        className='border border-slate-200 rounded-xl overflow-hidden group'
                      >
                        <div
                          onClick={() => toggleVessel(vesselName + '_copy')}
                          className='flex items-center justify-between p-4 bg-white cursor-pointer hover:bg-slate-50 transition-colors'
                        >
                          <div className='flex items-center gap-3'>
                            <div className='w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors'>
                              <Ship className='w-5 h-5' />
                            </div>
                            <div>
                              <div className='font-bold text-slate-800'>{vesselName}</div>
                              <div className='text-xs text-slate-500 mt-0.5'>
                                {vesselReports.length} budget items
                              </div>
                            </div>
                          </div>
                          <div className='flex items-center gap-4'>
                            <div className='text-right'>
                              <div className='text-xs text-slate-400 font-semibold mb-0.5'>
                                Total Budget
                              </div>
                              <div className='font-bold text-slate-900'>
                                {formatCurrency(totalBudget)}
                              </div>
                            </div>
                            <div
                              className={`text-slate-400 transition-transform ${
                                expandedVessels[vesselName + '_copy'] ? 'rotate-180' : ''
                              }`}
                            >
                              <ChevronDown className='w-5 h-5' />
                            </div>
                          </div>
                        </div>
                        {expandedVessels[vesselName + '_copy'] && (
                          <div className='p-4 bg-slate-50 border-t border-slate-200'>
                            <div className='space-y-2 mb-4'>
                              {vesselReports.map((rep) => (
                                <div
                                  key={rep.id}
                                  className='flex justify-between items-center text-sm'
                                >
                                  <span
                                    className='text-slate-600 truncate mr-2'
                                    title={rep.accountCode}
                                  >
                                    {rep.accountCode}
                                  </span>
                                  <span className='font-semibold text-slate-800 whitespace-nowrap'>
                                    {formatCurrency(rep.totalBudget)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <button
                              type='button'
                              onClick={() => handleCopyBudget(vesselName)}
                              className='w-full py-2 bg-blue-100 text-blue-700 hover:bg-blue-600 hover:text-white rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2'
                            >
                              <Copy className='w-4 h-4' /> Copy {vesselReports.length} Items to Form
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className='px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end'>
              <button
                onClick={() => setIsCopyModalOpen(false)}
                className='px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {activeDropdown &&
        (() => {
          const rep = reports.find((r: any) => r.id === activeDropdown)
          if (!rep) return null
          return (
            <AccountsActionDropdown open={true} position={dropdownPos}>
              {hasPermission(ACCOUNTS_PERMS.BUDGET_UPDATE) && (
                <button
                  className='w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2'
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditingBudget(rep)
                    setIsCreateModalOpen(true)
                    setActiveDropdown(null)
                    setDropdownPos(null)
                  }}
                >
                  <Edit className='w-4 h-4 text-slate-400' /> Edit Budget
                </button>
              )}
              {hasPermission(ACCOUNTS_PERMS.BUDGET_DELETE) && (
                <>
                  <div className='h-px bg-slate-100 my-1'></div>
                  <button
                    className='w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(e, rep.rawId)
                      setActiveDropdown(null)
                      setDropdownPos(null)
                    }}
                  >
                    <Trash2 className='w-4 h-4 text-red-400' /> Delete Budget
                  </button>
                </>
              )}
            </AccountsActionDropdown>
          )
        })()}
    </>
  )
}
